import { Button, StatusBadge, buttonClassName, Skeleton } from '@argink/ui';
import type { BridgeOnboardingState } from '../lib/bridge-onboarding';
import type { BridgeDownloadConfig } from '../lib/bridge-download';
import { StateCard } from './StateCard';

export interface BridgePanelProps {
  state: BridgeOnboardingState;
  /** "Verificar novamente" — nunca abre sessão, só redescobre o programa local (§12). */
  onRetry: () => void;
  /** "Baixar" / "Baixar novamente" — só chamado quando `downloadConfig` existe (o
   * próprio botão não é renderizado quando é `null`, ver §10). */
  onDownload: () => void;
  /** `null` = nenhum instalador publicado para este ambiente (`lib/bridge-download.ts`). */
  downloadConfig: BridgeDownloadConfig | null;
}

/**
 * Onboarding do **ARGINK Bridge**, na superfície do SIGNATÁRIO (R-UX02-P03, §7).
 *
 * Quem chega aqui recebeu um link por e-mail e quer assinar um documento. Ele não
 * sabe o que é extensão de navegador, host nativo, protocolo, porta ou PKCS#11 — e
 * nada disso aparece na tela. O que muda a cada fase é a AÇÃO que a pessoa consegue
 * executar sozinha, nunca um "algo deu errado" genérico:
 *
 * - nada instalado → baixar;
 * - baixando/instalando → a página observa sozinha e avança quando terminar;
 * - versão velha → atualizar;
 * - demorou demais ou algo falhou → verificar de novo, ou baixar de novo;
 * - tudo certo → some da tela ("Seus certificados digitais" assume, ver `CertList`
 *   em `SignPage`) — infraestrutura desaparece quando funciona.
 *
 * Nenhuma fase pede PIN, senha, arquivo de certificado ou chave privada: a posse e o
 * uso da chave são inteiramente do programa local (D-001, zero custódia).
 */
export function BridgePanel({ state, onRetry, onDownload, downloadConfig }: BridgePanelProps) {
  const retryAction = (
    <Button variant="outline" onClick={onRetry}>
      Verificar novamente
    </Button>
  );

  // Baixar é NAVEGAÇÃO, não ação: um `<button onClick>` perderia abrir em nova aba, o
  // menu de contexto e o anúncio de "link" no leitor de tela. `buttonClassName` existe
  // no design system exatamente para isso — a aparência continua sendo dele.
  function downloadAction(label: string) {
    if (!downloadConfig) return null;
    return (
      <a
        className={buttonClassName({ variant: 'primary' })}
        href={downloadConfig.url}
        rel="noreferrer"
        onClick={onDownload}
      >
        {label}
      </a>
    );
  }

  /** §10 — sem instalador publicado, a tela diz isso em vez de oferecer um clique
   * morto. Só aparece quando não há `downloadConfig`. */
  const noDownloadHint = !downloadConfig ? (
    <p className="state-card__hint">
      O download do ARGINK Bridge não está disponível neste ambiente agora. Peça o
      instalador a quem enviou este documento.
    </p>
  ) : null;

  switch (state.phase) {
    case 'CHECKING':
      return (
        <StateCard tone="info" title="Procurando seus certificados neste computador…">
          <p className="state-card__hint">Isso leva alguns segundos.</p>
        </StateCard>
      );

    case 'DISCOVERING_CERTIFICATES':
      // A pergunta que o usuário precisa ver ANTES de achar que travou: o ARGINK Bridge abre uma
      // janela do próprio Windows pedindo autorização para mostrar os certificados a esta página
      // (é decisão dele — a lista carrega nome, CPF e emissor). Essa janela costuma aparecer ATRÁS
      // do navegador. Sem esta linha, a tela dizia só "procurando" enquanto a pergunta esperava
      // fora de vista, e a pessoa não tinha como saber o que fazer (medido no Gate B, 09/09/2026).
      return (
        <StateCard tone="info" title="Autorize o acesso aos seus certificados">
          <p className="state-card__hint">
            O ARGINK Bridge abriu uma janela pedindo sua autorização para mostrar seus certificados
            nesta página. Ela pode estar atrás desta janela do navegador.
          </p>
        </StateCard>
      );

    case 'NOT_INSTALLED':
      return (
        <StateCard
          tone="error"
          title="Instale o ARGINK Bridge para usar seu certificado digital"
          detail="O ARGINK Bridge permite acessar com segurança os certificados deste computador. Sua chave privada permanece no dispositivo."
          action={
            <>
              {downloadAction('Baixar ARGINK Bridge')}
              {retryAction}
            </>
          }
        >
          {noDownloadHint}
        </StateCard>
      );

    case 'WAITING_FOR_INSTALL':
      return (
        <StateCard tone="info" title="Preparando seu certificado digital" action={retryAction}>
          <Skeleton lines={2} label="Aguardando a instalação do ARGINK Bridge…" />
          <p className="state-card__hint">Download iniciado.</p>
          <p className="state-card__hint">
            Esta página continuará automaticamente quando a instalação estiver concluída.
          </p>
        </StateCard>
      );

    case 'CONNECTING':
      return (
        <StateCard
          tone="info"
          title="Confirmando a instalação do ARGINK Bridge"
          action={
            <>
              {downloadAction('Baixar novamente')}
              {retryAction}
            </>
          }
        >
          <Skeleton lines={2} label="Quase lá — confirmando a instalação…" />
          <p className="state-card__hint">
            Esta página continuará automaticamente quando a instalação estiver concluída.
          </p>
        </StateCard>
      );

    case 'INCOMPATIBLE':
      return (
        <StateCard
          tone="error"
          title="Atualize o ARGINK Bridge para continuar"
          detail="A versão instalada neste computador não tem tudo que esta assinatura exige."
          action={
            <>
              {downloadAction('Baixar atualização')}
              {retryAction}
            </>
          }
        >
          {noDownloadHint}
        </StateCard>
      );

    case 'READY':
      // Brevemente, antes de a listagem de certificados assumir a tela (§7) — a
      // fase seguinte (DISCOVERING_CERTIFICATES) já é disparada automaticamente.
      return (
        <StatusBadge tone="info" icon="●">
          ARGINK Bridge conectado.
        </StatusBadge>
      );

    case 'RECOVERABLE_ERROR':
      // Só o timeout de INSTALAÇÃO tem as duas ações aqui ("Verificar novamente" +
      // "Baixar novamente" — o programa ainda não respondeu, então reinstalar é uma
      // saída plausível). Uma falha em LISTAR certificados com o programa já pronto
      // (`certificates_failed`) é outro problema — `CertList`, em `SignPage`, já tem
      // a UI de erro certa para ela (uma ação, "Tentar de novo"), e duplicar aqui só
      // ofereceria "Baixar de novo" para algo que baixar de novo não resolve.
      if (state.error?.reason !== 'poll_timeout') return null;
      return (
        <StateCard
          tone="error"
          title="Ainda não conseguimos detectar a instalação."
          detail="Confira se a instalação foi concluída, ou tente baixar de novo."
          action={
            <>
              {downloadAction('Baixar novamente')}
              {retryAction}
            </>
          }
        >
          {noDownloadHint}
        </StateCard>
      );

    // CERTIFICATES_READY / NO_COMPATIBLE_CERTIFICATE: o programa já está pronto e a pessoa já
    // autorizou — a tela passa a falar só de certificados (`CertList`, em `SignPage`).
    // Infraestrutura desaparece quando funciona.
    default:
      return null;
  }
}
